import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { logEvent } from '../new-lib/analytics';

interface UseVideoOptions {
  videoRef: React.RefObject<HTMLVideoElement>;
  propertyData?: {
    property_id: number;
    compound_id: number;
    compound_name: string;
    sale_type: string;
    property_type: string;
  };
  setIsVideoExpanded: (expanded: boolean) => void;
  eventName?: string;
  featureName?: string;
}

export const useVideo = ({
  videoRef,
  propertyData,
  setIsVideoExpanded,
  eventName,
  featureName
}: UseVideoOptions) => {
  const isMobile = useSelector((state: any) => state.app.isMobile);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (document.fullscreenElement === null) {
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }
        setIsVideoExpanded(false);
      }
    };

    const handleIOSExit = () => {
      if (videoRef.current) {
        const v = videoRef.current;
        v.pause();
        v.currentTime = 0;
        v.style.visibility = 'hidden';
        v.style.width = '1px';
        v.style.height = '1px';
        v.style.position = 'static';
        v.style.top = '';
        v.style.left = '';
        v.style.zIndex = '';
      }
      setIsVideoExpanded(false);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    if (videoRef.current) {
      (videoRef.current as any).addEventListener(
        'webkitendfullscreen',
        handleIOSExit
      );
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );

      if (videoRef.current) {
        (videoRef.current as any).removeEventListener(
          'webkitendfullscreen',
          handleIOSExit
        );
      }
    };
  }, [videoRef, setIsVideoExpanded]);

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleFullScreenVideo = (buttonLocation?: string) => {
    if (videoRef.current) {
      const video = videoRef.current;
      video.playsInline = true;
      video.controls = true;

      if (isMobile && isIOS()) {
        video.style.visibility = 'visible';
        video.style.width = '100vw';
        video.style.height = '100vh';
        video.style.position = 'fixed';
        video.style.top = '0';
        video.style.left = '0';
        video.style.zIndex = '9999';

        try {
          video.play();
        } catch (err) {
          return err;
        }

        // Immediately enter fullscreen
        if ((video as any).webkitEnterFullscreen) {
          (video as any).webkitEnterFullscreen();
        } else if ((video as any).webkitRequestFullscreen) {
          (video as any).webkitRequestFullscreen();
        }
      } else {
        // Desktop & Android
        if (video.requestFullscreen) {
          video.requestFullscreen();
        } else if ((video as any).webkitRequestFullscreen) {
          (video as any).webkitRequestFullscreen();
        } else if ((video as any).msRequestFullscreen) {
          (video as any).msRequestFullscreen();
        }
        video.play();
      }

      setIsVideoExpanded(true);
      if (eventName && buttonLocation && featureName) {
        logEvent.click(eventName, buttonLocation, featureName, {
          prop_id: propertyData?.property_id,
          cmpnd_id: propertyData?.compound_id,
          cmpnd_name: propertyData?.compound_name,
          sale_type: propertyData?.sale_type,
          prop_type_name: propertyData?.property_type
        });
      }
    }
  };

  return { handleFullScreenVideo };
};
